import { browserLogger } from '@newfront-insurance/dd-rum';
import { AuthOptions } from '@newfront-insurance/ms-graph-auth';
import { useCallback, useEffect, useState } from 'react';

import { msGraphManager } from '../../../services/ms-graph-manager';
import { useMSGraphAuth } from '../contexts/ms-graph-context';

export interface OutlookConnectController {
  // State
  outlookConnectEnabled: boolean;

  // Actions
  setOutlookConnectEnabled(enabled: boolean): void;
}

export function useOutlookConnectController({
  msGraphEnabled,
  isChatVisible,
  notify,
}: {
  msGraphEnabled: boolean;
  isChatVisible: boolean;
  notify: { success: (msg: string) => void; error: (msg: string) => void };
}): OutlookConnectController {
  const msGraphAuth = useMSGraphAuth();

  const [outlookConnectEnabled, setOutlookConnectEnabledState] = useState(() => msGraphManager.isEnabled());

  // Initialize MS Graph Manager with feature flag
  useEffect(() => {
    msGraphManager.setFeatureFlagEnabled(msGraphEnabled);
  }, [msGraphEnabled]);

  // Feature flag kill switch - automatically disconnect when flag is OFF
  useEffect(() => {
    if (!msGraphEnabled && msGraphAuth && msGraphAuth.authState.state === AuthOptions.LOGGED_IN) {
      msGraphManager.disable();
      setOutlookConnectEnabledState(false);
    }
  }, [msGraphEnabled, msGraphAuth]);

  // Proactive re-authentication: When Nova window is opened and toggle is ON but user is logged out, trigger login
  // This ensures toggle state always matches auth state when user actively opens Nova (toggle ON = authenticated)
  const authStateValue = msGraphAuth?.authState.state;

  useEffect(() => {
    if (
      isChatVisible && // Only when Nova chat window is visible (user deliberately opened it)
      outlookConnectEnabled &&
      msGraphAuth &&
      (authStateValue === AuthOptions.LOGGED_OUT || authStateValue === AuthOptions.INTERACTION_REQUIRED)
    ) {
      browserLogger.info(
        '[NovaProvider] Nova opened with toggle ON but user logged out - triggering re-authentication',
        { authState: authStateValue },
      );
      msGraphAuth
        .login()
        .then((authState) => {
          browserLogger.info('[NovaProvider] Re-authentication completed', {
            newAuthState: authState.state,
            wasSuccessful: authState.state === AuthOptions.LOGGED_IN,
          });
          if (authState.state === AuthOptions.LOGGED_IN) {
            notify.success('Outlook connected successfully');
          }
        })
        .catch((error) => {
          browserLogger.error('[NovaProvider] Re-authentication failed', error);
          // Don't disable toggle on error - user might have just cancelled
        });
    }
  }, [isChatVisible, outlookConnectEnabled, authStateValue, msGraphAuth, notify]);

  // Disable Outlook and reset state
  const disableOutlook = useCallback(() => {
    msGraphManager.disable();
    setOutlookConnectEnabledState(false);
  }, []);

  // Handle authentication cancellation
  const handleAuthCancelled = useCallback(() => {
    disableOutlook();
    notify.error('Outlook authentication was cancelled. Please enable again in Settings.');
  }, [disableOutlook, notify]);

  // Handle authentication failure
  const handleAuthError = useCallback(
    (error: Error) => {
      disableOutlook();
      notify.error(`Outlook authentication failed: ${error.message || 'Please try again.'}`);
    },
    [disableOutlook, notify],
  );

  // Enable Outlook and trigger authentication
  const enableOutlook = useCallback(async () => {
    msGraphManager.enable();
    setOutlookConnectEnabledState(true);

    if (!msGraphAuth) {
      return;
    }

    const isLoggedIn = msGraphAuth.authState.state === AuthOptions.LOGGED_IN;
    if (isLoggedIn) {
      // Already logged in - no toast needed, user just toggled ON
      return;
    }

    try {
      const authState = await msGraphAuth.login();
      if (authState.state === AuthOptions.LOGGED_IN) {
        // Show toast only after successful authentication
        notify.success('Outlook connected successfully');
      } else {
        handleAuthCancelled();
      }
    } catch (error) {
      handleAuthError(error as Error);
    }
  }, [msGraphAuth, handleAuthCancelled, handleAuthError, notify]);

  // Handle Outlook Connect toggle changes
  const setOutlookConnectEnabled = useCallback(
    (enabled: boolean) => {
      if (enabled) {
        enableOutlook();
      } else {
        disableOutlook();
      }
    },
    [enableOutlook, disableOutlook],
  );

  return {
    outlookConnectEnabled,
    setOutlookConnectEnabled,
  };
}
